"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";

const companyTypes: { value: string; label: string }[] = [
  { value: "bank", label: "Bank" },
  { value: "broker", label: "Broker" },
  { value: "asset-manager", label: "Asset Manager" },
  { value: "family-office", label: "Family Office" },
  { value: "multi-family-office", label: "Multi Family Office" },
  { value: "exchange", label: "Exchange" },
];

const countries = [
  "Argentina",
  "Australia",
  "Austria",
  "Belgium",
  "Brazil",
  "Canada",
  "Chile",
  "China",
  "Colombia",
  "Denmark",
  "Finland",
  "France",
  "Germany",
  "Hong Kong",
  "India",
  "Ireland",
  "Israel",
  "Italy",
  "Japan",
  "Luxembourg",
  "Mexico",
  "Netherlands",
  "New Zealand",
  "Norway",
  "Peru",
  "Portugal",
  "Singapore",
  "South Africa",
  "South Korea",
  "Spain",
  "Sweden",
  "Switzerland",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Other",
];

const API_BASE_URL = "/api";

export default function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [country, setCountry] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!showModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) {
        setShowModal(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal]);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setShowModal(true);
    }
  };

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !companyType || !country) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/contact-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requests: [{ email, name, company_type: companyType, country }] }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message ?? "Something went wrong. Please try again.");
      }

      setShowModal(false);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setEmail("");
        setName("");
        setCompanyType("");
        setCountry("");
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="max-w-md mx-auto">
        {!submitted ? (
          <form onSubmit={handleEmailSubmit} className="relative">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-14 pl-6 pr-36 bg-card border-none text-foreground placeholder:text-muted-foreground/70 rounded-full focus-visible:ring-primary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
            <Button
              type="submit"
              className="absolute right-1 top-1 h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-6"
            >
              Contact Us
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="h-14 bg-card rounded-full flex items-center justify-center text-primary"
          >
            Thank you! We will be in touch soon.
          </motion.div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="waitlist-modal-title"
              className="bg-card rounded-2xl p-8 w-full max-w-md relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowModal(false);
                }}
                aria-label="Close"
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10 p-2"
              >
                <X className="h-5 w-5" />
              </button>

              <h2 id="waitlist-modal-title" className="text-2xl font-bold text-foreground mb-2">
                Complete Your Information
              </h2>
              <p className="text-muted-foreground mb-6">
                Tell us more about yourself and your company.
              </p>

              <form onSubmit={handleFinalSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">
                    Name
                  </label>
                  <Input
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="h-12 bg-background border-border text-foreground placeholder:text-muted-foreground/50 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm text-muted-foreground mb-2">
                    Company Type
                  </label>
                  <Select value={companyType} onValueChange={setCompanyType} required>
                    <SelectTrigger className="h-12 bg-background border-border text-foreground rounded-lg">
                      <SelectValue placeholder="Select company type" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-60">
                      {companyTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm text-muted-foreground mb-2">
                    Country
                  </label>
                  <Select value={country} onValueChange={setCountry} required>
                    <SelectTrigger className="h-12 bg-background border-border text-foreground rounded-lg">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-60">
                      {countries.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {error && (
                  <p className="text-sm text-destructive mt-2">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground mt-6 disabled:opacity-60"
                >
                  {loading ? "Submitting..." : "Submit"}
                  {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
